import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { BranchCode } from "api/risk";
import { useRgiRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { useRealEstateRequest } from "./useRealEstateRequest";
import { RGIStepIdentifier } from "features/subscription-process/types/enums";
import { TenantMinimalDto } from "api/real-estate";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { usePricingParc } from "features/subscription-process/pricing-process/hooks/usePricingParc";
import { BranchStepConfigFactory, createStepConfig } from "features/subscription-process/navigation/Steps/createStepConfig";

export const useTenantRegistrationPage = (branchCode: BranchCode) => {
  // fetchSubscriptionProcess is called by SubscriptionProcessWrapper - no need to call it here
  const { riskId } = useParams<{ riskId: string }>();
  const { fetchRealEstateById } = useRealEstateRequest(branchCode)

  const { t } = useDynamicTranslation([NsKey.Common, NsKey.RealEstate]);
  // Tunnel parc : les locataires se chargent BIEN PAR BIEN (ParcTenancyPage) —
  // l'endpoint locataires sans bienId est invalide pour un risque parc (500
  // back), on ne le sollicite donc jamais tant que la nature du risque n'est
  // pas connue, ni quand c'est un parc.
  const { isParc, isLoading: isParcLoading } = usePricingParc(riskId);
  const riskData = useRgiRiskData();
  const realEstate = riskData?.realEstate
  const contractStartDate = riskData?.contractStartDate

  // États locaux
  const [isLoading, setIsLoading] = useState(false);
  const [error] = useState<string | null>(null);

  const [tenants, setTenants] = useState<TenantMinimalDto[]>([])

  const loadTenants = useCallback(async () => {
    if (!realEstate || !riskId || isParcLoading || isParc) return;
    try {
      setIsLoading(true)
      const response = await realEstateAPI.getTenants({ riskId })
      if (response && response.tenants) {
        setTenants(response.tenants)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [realEstate, riskId, isParc, isParcLoading])

  useEffect(() => {
    loadTenants()
  }, [loadTenants])

  const isEligible = useMemo(() => {
    return tenants.some(tenant => tenant.isSolvable)
  }, [tenants])

  useEffect(() => {
    if (realEstate) {
      fetchRealEstateById(realEstate.id);
    }
  }, [realEstate, fetchRealEstateById]);

  const stepPageConfig: Partial<Record<BranchCode, BranchStepConfigFactory>> = useMemo(() => ({
    [BranchCode.RGI]: createStepConfig({
      next: {
        identifier: RGIStepIdentifier.Pricing,
        disabled: !isEligible,
        route: "/simulation/tarification",
      },
      prev: {
        identifier: RGIStepIdentifier.RealEstateRegistration,
        route: "/simulation/inscription/gli/biens",
      },
    })
  }), [isEligible]);

  return {
    // États
    realEstate,
    isLoading,
    isParc,
    isParcLoading,
    error,

    // Configurations (sans JSX)
    stepPageConfig,

    // Utilitaires
    t,

    loadTenants,
    contractStartDate,
    tenants,
    riskId
  };
};
